import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { GroupSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { groupsApi } from '@/services/groups.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';
import { GroupForm } from './group-form';

/**
 * The Groups directory.
 *
 * Deliberately the same shape as People's: same search behaviour, same archived
 * toggle, same empty and error states. Two screens that do the same job should
 * not look like they were built by two different teams, and a member of staff
 * who has learned one has learned both.
 */
export function GroupsListPage() {
  const { t } = useI18n();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  // Controlled exclusively by My Settings — no per-page override, so an
  // archived record is never one accidental checkbox away from view.
  const showArchived = getShowArchivedRecordsPreference();

  // Lags one render behind while typing, so a keystroke does not queue a
  // request and the results do not flicker through intermediate states.
  const deferredSearch = useDeferredValue(search);

  const query = useQuery({
    queryKey: ['groups', { search: deferredSearch, showArchived }],
    queryFn: ({ signal }) =>
      groupsApi.list(
        { search: deferredSearch || undefined, includeArchived: showArchived, limit: 50 },
        signal,
      ),
    placeholderData: (previous) => previous,
  });

  const groups = query.data?.data ?? [];
  const isStale = query.isPlaceholderData;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('groups.group.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('groups.group.count', { count: groups.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>

        <div className="page-actions">
          <Link to="/groups/hierarchy" className="btn" data-variant="secondary">
            {t('groups.group.viewHierarchy')}
          </Link>
          <Link to="/groups/map" className="btn" data-variant="secondary">
            {t('groups.group.viewMap')}
          </Link>
          {can('groups.group.create') && (
            <button
              type="button"
              className="btn"
              data-variant="primary"
              onClick={() => setIsCreating(true)}
            >
              {t('groups.group.create')}
            </button>
          )}
        </div>
      </header>

      <div className="filters">
        <Field
          label={t('common.actions.search')}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('common.actions.search')}
          name="search"
        />
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && groups.length === 0 && (
        <EmptyState
          title={search ? t('common.states.noResults') : t('groups.group.empty')}
          hint={search ? undefined : t('groups.group.create')}
        />
      )}

      {groups.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('groups.group.singular')}</th>
                <th scope="col">{t('groups.group.members')}</th>
                <th scope="col">{t('groups.group.meetingDay')}</th>
                <th scope="col">{t('common.labels.status')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <GroupRow key={group.id} group={group} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <GroupForm
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function GroupRow({ group }: { group: GroupSummaryResponse }) {
  const { t } = useI18n();

  return (
    <tr data-archived={group.archivedAt ? true : undefined}>
      <td>
        <Link to={`/groups/${group.id}`} className="person-name">
          {group.name}
        </Link>
      </td>
      <td>
        {t('groups.group.memberCount', { count: group.memberCount })}
        {/* The limit is shown beside the count, because "12 members" means
            something different in a group that seats twelve. */}
        {group.capacity !== null && <span className="cell-empty"> / {group.capacity}</span>}
      </td>
      <td>
        {group.meetingDay ? (
          <>
            {t(`groups.day.${group.meetingDay}`)}
            {group.meetingTime && <span dir="ltr"> · {group.meetingTime}</span>}
          </>
        ) : (
          <span className="cell-empty">—</span>
        )}
      </td>
      <td>
        <StatusBadge status={group.status} label={t(`groups.status.${group.status}`)} />
      </td>
    </tr>
  );
}
